import * as fs from 'fs'
import { join, isAbsolute } from 'path'
import { ErrorEventInstanceObj, FunctionInstanceObj } from './objects.js'
import { ConsumerStream, NatsStreamService } from './NatsStreamingService.js'
import { filter } from 'rxjs/operators'
import { IObjSubjectData, IEangEvent } from './functions.js'
import { logger } from './logger.js'
import { NatsConnection } from '@nats-io/nats-core'
import {
  JetStreamClient,
  JetStreamManager,
  JetStreamPublishOptions
} from '@nats-io/jetstream'
import {
  FunctionStartContext,
  IFunctionStartContext,
  IFunctionStopContext
} from './context.js'
import { EntityEvent } from './entity.js'
import { Subscription } from 'rxjs'
import * as v from 'valibot'

// Configuration utility class to centralize config merging logic
class ConfBuilder {
  static buildBaseConfig<
    TEnv = { [key: string]: string },
    TSecrets = { [key: string]: string }
  >(conf?: EangConfig<TEnv, TSecrets>) {
    // Handle EANG_SERVERS as JSON array if from environment
    let servers = conf?.servers
    if (!servers && process.env.EANG_SERVERS) {
      try {
        const serversArray = JSON.parse(process.env.EANG_SERVERS)
        if (Array.isArray(serversArray)) {
          servers = serversArray.join(',')
        } else {
          servers = process.env.EANG_SERVERS
        }
      } catch {
        // Fallback to treating as comma-separated string
        servers = process.env.EANG_SERVERS
      }
    }

    const configToValidate = {
      tenant: conf?.tenant || process.env.EANG_TENANT,
      organizationalUnit:
        conf?.organizationalUnit || process.env.EANG_ORGANIZATIONAL_UNIT,
      servers: servers,
      user: conf?.user || process.env.EANG_USER,
      password: conf?.password || process.env.EANG_PASSWORD,
      secretsFilePath: conf?.secretsFilePath || process.env.EANG_SECRETS_FILE_PATH,
      logLevel: conf?.logLevel || process.env.EANG_LOG_LEVEL || 'info',
      secrets: conf?.secrets,
      secretsSchema: conf?.secretsSchema,
      env: conf?.env,
      envSchema: conf?.envSchema
    }

    if (!configToValidate.password) {
      // Load secrets if password is not provided and no secrets object is given
      if (!configToValidate.secrets) {
        const path =
          configToValidate.secretsFilePath ||
          (fs.existsSync('/run/secrets/secrets.json')
            ? '/run/secrets/secrets.json'
            : './secrets/secrets.json')
        configToValidate.secretsFilePath = path
        const { secrets: loadedSecrets, eangPassword } = this.loadSecrets<TSecrets>(
          path,
          configToValidate.secretsSchema
        )
        if (eangPassword) {
          configToValidate.password = eangPassword
        }
        configToValidate.secrets = loadedSecrets
      } else if (
        configToValidate.secrets &&
        typeof configToValidate.secrets === 'object' &&
        'EANG_PASSWORD' in configToValidate.secrets
      ) {
        configToValidate.password = (configToValidate.secrets as any).EANG_PASSWORD
      }
    }

    return configToValidate
  }

  static buildFunctionConfig<
    TEnv = { [key: string]: string },
    TSecrets = { [key: string]: string }
  >(conf?: EangFunctionConfig<TEnv, TSecrets>) {
    const baseConfig = this.buildBaseConfig<TEnv, TSecrets>(conf)

    return {
      ...baseConfig,
      instanceOf: conf?.instanceOf || process.env.EANG_INSTANCE_OF
    }
  }

  static buildServiceConfig<
    TSecrets = { [key: string]: string },
    TEnv = { [key: string]: string }
  >(conf?: EangServiceConfig<TSecrets, TEnv>) {
    const baseConfig = this.buildBaseConfig<TSecrets, TEnv>(conf)

    let subscriptions = conf?.subscriptions
    if (!subscriptions) {
      const subscriptionString = process.env.EANG_SUBSCRIPTIONS
      if (subscriptionString && subscriptionString.length > 0) {
        // Parse as comma-separated values
        subscriptions = subscriptionString
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
      }
    }

    return {
      ...baseConfig,
      instanceOf: conf?.instanceOf || process.env.EANG_INSTANCE_OF,
      subscriptions: subscriptions || []
    }
  }

  static loadSecrets<TSecrets = { [key: string]: string }>(
    filePath: string,
    schema?: v.BaseSchema<any, TSecrets, any>
  ): { secrets: TSecrets | undefined; eangPassword: string | undefined } {
    const absolutePath = isAbsolute(filePath) ? filePath : join(process.cwd(), filePath)

    try {
      if (fs.existsSync(absolutePath)) {
        const secretsContent = fs.readFileSync(absolutePath, 'utf-8')
        const rawSecrets = JSON.parse(secretsContent)
        if (
          !rawSecrets ||
          typeof rawSecrets !== 'object' ||
          Object.keys(rawSecrets).length === 0
        ) {
          logger.error(`Secrets file '${absolutePath}' is empty or invalid`)
        }

        // Extract EANG_PASSWORD before validation
        const eangPassword =
          'EANG_PASSWORD' in rawSecrets ? String(rawSecrets.EANG_PASSWORD) : undefined

        // Validate with schema if provided
        if (schema) {
          try {
            const validatedSecrets = v.parse(schema, rawSecrets)
            return { secrets: validatedSecrets, eangPassword }
          } catch (error) {
            if (error instanceof v.ValiError) {
              throw this.formatValiError(error, 'Secrets')
            }
            throw error
          }
        }

        // Ensure all values are strings (default behavior when no schema)
        const processedSecrets: { [key: string]: string } = {}
        for (const [key, value] of Object.entries(rawSecrets)) {
          processedSecrets[key] = String(value)
        }

        return { secrets: processedSecrets as TSecrets, eangPassword }
      } else {
        logger.error(`Secrets file '${absolutePath}' not found!`)
      }
      return { secrets: undefined, eangPassword: undefined }
    } catch (error) {
      if (error instanceof SyntaxError) {
        logger.error(
          error,
          `Failed to parse secret file '${absolutePath}': Invalid JSON format`
        )
      } else {
        console.log(error)
        logger.error(error, 'Failed to load secrets: Unknown error')
      }
      return { secrets: undefined, eangPassword: undefined }
    }
  }
  static formatValiError(error: v.ValiError<any>, contextName: string): Error {
    const errorMessages = error.issues
      .map((issue: any) => {
        const path = issue.path?.map((p: { key: string }) => p.key).join('.') || 'root'
        return `${path}: ${issue.message}`
      })
      .join('; ')
    return new Error(`${contextName} configuration validation failed: ${errorMessages}`)
  }

  static validateConfig<T>(
    schema: v.BaseSchema<any, T, any>,
    config: any,
    contextName: string
  ): T {
    try {
      return v.parse(schema, config)
    } catch (error) {
      if (error instanceof v.ValiError) {
        throw this.formatValiError(error, contextName)
      }
      throw error
    }
  }
}

export type EangFunctionHandler<
  TInput extends object = any,
  TOutput extends object = any,
  TEnv = { [key: string]: string },
  TSecrets = { [key: string]: string }
> = (
  input: TInput,
  eang: EangFunction<TEnv, TSecrets>,
  context?: FunctionStartContext
) => Promise<IFunctionStopContext<TOutput>>

export interface IPublishingOptions {
  tenant: string
  organizationalUnit?: string
  user: string
  jetStreamOptions?: Partial<JetStreamPublishOptions>
}

/**
 * Configuration for Eang classes with optional type-safe secrets and environment variables.
 *
 * All fields are optional and will be loaded from environment variables if not provided:
 * - tenant: env.EANG_TENANT
 * - organizationalUnit: env.EANG_ORGANIZATIONAL_UNIT
 * - servers: env.EANG_SERVERS
 * - user: env.EANG_USER
 * - password: env.EANG_PASSWORD
 *
 * @example
 * // Provide only secretsSchema, everything else from environment
 * const publisher = new EangPublisher<never, MySecrets>({
 *   secretsSchema: MySecretsSchema
 * })
 *
 * @example
 * // Type-safe environment variables only
 * const publisher = new EangPublisher<MyEnv>({
 *   envSchema: MyEnvSchema
 * })
 *
 * @example
 * // Both type-safe env and secrets
 * const publisher = new EangPublisher<MyEnv, MySecrets>({
 *   envSchema: MyEnvSchema,
 *   secretsSchema: MySecretsSchema
 * })
 */
export interface EangConfig<
  TEnv = { [key: string]: string },
  TSecrets = { [key: string]: string }
> {
  tenant?: string // default: env.EANG_TENANT
  organizationalUnit?: string | undefined // default: env.EANG_ORGANIZATIONAL_UNIT
  servers?: string // default: env.EANG_SERVERS
  user?: string // default: env.EANG_USER
  password?: string // default: env.EANG_PASSWORD (from secrets)
  secretsFilePath?: string // default: env.EANG_SECRETS_FILE_PATH
  secrets?: TSecrets // optional secrets object to override file loading
  secretsSchema?: v.BaseSchema<any, TSecrets, any> // optional valibot schema for secrets validation
  env?: TEnv // optional environment variables object
  envSchema?: v.BaseSchema<any, TEnv, any> // optional valibot schema for env validation
  logLevel?: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent' // default: info
}

export interface EangFunctionConfig<
  TEnv = { [key: string]: string },
  TSecrets = { [key: string]: string }
> extends EangConfig<TEnv, TSecrets> {
  instanceOf?: string // default: env.EANG_INSTANCE_OF
}

export interface EangServiceConfig<
  TEnv = { [key: string]: string },
  TSecrets = { [key: string]: string }
> extends EangConfig<TEnv, TSecrets> {
  instanceOf?: string // default: env.EANG_INSTANCE_OF
  subscriptions?: string[] // default: env.EANG_SUBSCRIPTIONS (comma separated)
}

// Valibot validation schemas
const EangConfigSchema = v.object({
  tenant: v.pipe(v.string(), v.minLength(1, 'Tenant cannot be empty')),
  organizationalUnit: v.optional(v.string()),
  servers: v.pipe(v.string(), v.minLength(1, 'Servers cannot be empty')),
  user: v.pipe(v.string(), v.minLength(1, 'User cannot be empty')),
  password: v.pipe(v.string(), v.minLength(1, 'Password cannot be empty')),
  secretsFilePath: v.optional(
    v.pipe(v.string(), v.minLength(1, 'Secrets file path cannot be empty'))
  ),
  secrets: v.optional(v.any()),
  secretsSchema: v.optional(v.any()),
  env: v.optional(v.any()),
  envSchema: v.optional(v.any()),
  logLevel: v.pipe(v.picklist(['trace', 'debug', 'info', 'warn', 'error', 'silent']))
})

const EangFunctionConfigSchema = v.object({
  ...EangConfigSchema.entries,
  instanceOf: v.pipe(v.string(), v.minLength(1, 'instanceOf cannot be empty'))
})

const EangServiceConfigSchema = v.object({
  ...EangFunctionConfigSchema.entries,
  subscriptions: v.pipe(
    v.array(v.string()),
    v.minLength(1, 'At least one subscription is required')
  )
})

export abstract class EangBase<
  TEnv = { [key: string]: string },
  TSecrets = { [key: string]: string }
> {
  public tenant: string
  public organizationalUnit?: string | undefined
  public servers: string
  public user: string
  protected _password: string
  protected _secretsFilePath: string | undefined
  protected _secretsSchema: v.BaseSchema<any, TSecrets, any> | undefined
  protected _logLevel: string
  protected _secrets: TSecrets | undefined
  protected _env: TEnv | undefined
  protected _envSchema: v.BaseSchema<any, TEnv, any> | undefined
  protected _nss = new NatsStreamService()
  protected _natsConnection:
    | { nc: NatsConnection; js: JetStreamClient; jsm: JetStreamManager }
    | undefined

  public log = logger

  get secrets(): TSecrets {
    if (!this._secrets) {
      const path =
        this._secretsFilePath ||
        (fs.existsSync('/run/secrets/secrets.json')
          ? '/run/secrets/secrets.json'
          : './secrets/secrets.json')
      const { secrets } = ConfBuilder.loadSecrets<TSecrets>(path, this._secretsSchema)
      this._secrets = secrets
    }
    return this._secrets || ({} as TSecrets)
  }

  get env(): TEnv {
    if (!this._env) {
      // If schema is provided, validate process.env against it
      if (this._envSchema) {
        try {
          this._env = v.parse(this._envSchema, process.env)
        } catch (error) {
          if (error instanceof v.ValiError) {
            throw ConfBuilder.formatValiError(error, 'Environment variables')
          }
          throw error
        }
      }
    }
    return this._env || ({} as TEnv)
  }

  protected get natConf() {
    return { servers: this.servers, user: this.user, pass: this._password }
  }

  constructor(conf: EangConfig<TEnv, TSecrets>) {
    // After validation, these fields are guaranteed to exist
    this.tenant = conf.tenant!
    this.organizationalUnit = conf.organizationalUnit
    this.servers = conf.servers!
    this.user = conf.user!
    this._password = conf.password!
    this._secretsFilePath = conf.secretsFilePath
    this._secretsSchema = conf.secretsSchema
    this._logLevel = conf.logLevel || 'info'
    if (conf.secrets) {
      this._secrets = conf.secrets
    }
    if (conf.env) {
      this._env = conf.env
    }
    this._envSchema = conf.envSchema
  }

  async ensureStream(streamName: string, subjects: string[]) {
    if (!this._natsConnection) {
      const { nc, js, jsm } = await this._nss.initialize(this.natConf)
      this._natsConnection = { nc, js, jsm }
    }
    const stream = await this._nss.ensureStream(streamName, subjects)
    return stream
  }

  async publish(events: EntityEvent[], options?: IPublishingOptions) {
    const opts = options || ({} as IPublishingOptions)
    if (!opts.tenant) {
      opts.tenant = this.tenant
    }
    if (!opts.organizationalUnit && this.organizationalUnit) {
      opts.organizationalUnit = this.organizationalUnit
    }
    if (!opts.user) {
      opts.user = this.user
    }

    if (!this._natsConnection) {
      this._natsConnection = await this._nss.initialize(this.natConf)
    }
    const pubAcks = await this._nss.publish(events, opts)
    return pubAcks
  }

  async shutdown() {
    this.log.debug('In eang shutdown')
    if (this._natsConnection) {
      const { nc } = this._natsConnection
      await nc.drain()
      await nc.close()
      this._natsConnection = undefined
    }
  }
}

export class EangPublisher<
  TEnv = { [key: string]: string },
  TSecrets = { [key: string]: string }
> extends EangBase<TEnv, TSecrets> {
  constructor(conf?: EangConfig<TEnv, TSecrets>) {
    const confToValidate = ConfBuilder.buildBaseConfig<TEnv, TSecrets>(conf)
    const validatedConf = ConfBuilder.validateConfig(
      EangConfigSchema,
      confToValidate,
      'EangPublisher'
    )
    // Store secrets, env and schemas separately to maintain type safety
    const { secrets, secretsSchema, env, envSchema } = confToValidate
    const baseConf: any = { ...validatedConf }
    if (secrets !== undefined) baseConf.secrets = secrets
    if (secretsSchema !== undefined) baseConf.secretsSchema = secretsSchema
    if (env !== undefined) baseConf.env = env
    if (envSchema !== undefined) baseConf.envSchema = envSchema
    super(baseConf as EangConfig<TEnv, TSecrets>)
  }
}

export class EangFunction<
  TEnv = { [key: string]: string },
  TSecrets = { [key: string]: string }
> extends EangBase<TEnv, TSecrets> {
  public instanceOf: string
  private _subscription: Subscription | undefined
  private _handler: EangFunctionHandler<any, any, TEnv, TSecrets>

  constructor(options: {
    conf?: EangFunctionConfig<TEnv, TSecrets>
    handler: EangFunctionHandler<any, any, TEnv, TSecrets>
  }) {
    const confToValidate = ConfBuilder.buildFunctionConfig<TEnv, TSecrets>(options.conf)
    const validatedConf = ConfBuilder.validateConfig(
      EangFunctionConfigSchema,
      confToValidate,
      'EangFunction'
    )
    // Store secrets, env and schemas separately to maintain type safety
    const { secrets, secretsSchema, env, envSchema } = confToValidate
    const baseConf: any = { ...validatedConf }
    if (secrets !== undefined) baseConf.secrets = secrets
    if (secretsSchema !== undefined) baseConf.secretsSchema = secretsSchema
    if (env !== undefined) baseConf.env = env
    if (envSchema !== undefined) baseConf.envSchema = envSchema
    super(baseConf as EangConfig<TEnv, TSecrets>)
    this.instanceOf = validatedConf.instanceOf
    this._handler = options.handler
  }

  async start(): Promise<void> {
    if (!this._natsConnection) {
      this._natsConnection = await this._nss.initialize({
        servers: this.servers,
        user: this.user,
        pass: this._password
      })
    }

    const stream = await this._nss.ensureConsumerStream(
      `${this.tenant}_FunctionInstance_${this.instanceOf}`,
      [`eang.obj.${this.tenant}.*.*.FunctionInstance.${this.instanceOf}.>`]
    )

    this._subscription = stream.event$
      .pipe(
        filter(
          (m: IEangEvent) =>
            m.subjectData.eventType === 'start' &&
            m.subjectData.typeOf === 'FunctionInstance'
        )
      )
      .subscribe(async (e) => {
        // let startContext: FunctionStartContext | undefined = undefined
        if (e.context) {
          const startContext = new FunctionStartContext(
            e.context as IFunctionStartContext
          )
          this.log.debug(startContext)
          const input = startContext?.data
          let stopContext: IFunctionStopContext<any>
          try {
            stopContext = await this._handler(input ?? {}, this, startContext)
          } catch (error) {
            this.log.error(error, 'Unhandled error during function handler execution:')
            stopContext = {
              errEvents: [
                new ErrorEventInstanceObj({
                  name: 'UnhandledError',
                  instanceOf: `ErrorEvent/${this.instanceOf}`,
                  message: (error as Error).message,
                  data: {
                    errorMessage: (error as Error).message,
                    stack: (error as Error).stack
                  }
                })
              ]
            }
          }
          this.log.debug(stopContext)
          const subjectData = e.subjectData as IObjSubjectData
          const functionStopEvent = EntityEvent.stop(
            new FunctionInstanceObj({
              key: subjectData.key,
              instanceOf: subjectData.instanceOf
            }),
            stopContext
          )
          const pubAcks = await this.publish([functionStopEvent])
          this.log.debug(pubAcks)
          const ackAck = await e.msg.ackAck()
          this.log.debug(ackAck)
        } else {
          throw new Error('No context found')
        }
      })
    return stream.startToConsume()
    // return stream
  }

  override async shutdown() {
    await super.shutdown()
    if (this._subscription) {
      this._subscription.unsubscribe()
      this._subscription = undefined
    }
  }
}

export class EangService<
  TEnv = { [key: string]: string },
  TSecrets = { [key: string]: string }
> extends EangBase<TEnv, TSecrets> {
  public instanceOf: string
  public subscriptions: string[]

  constructor(conf?: EangServiceConfig<TEnv, TSecrets>) {
    const confToValidate = ConfBuilder.buildServiceConfig<TEnv, TSecrets>(conf)
    const validatedConf = ConfBuilder.validateConfig(
      EangServiceConfigSchema,
      confToValidate,
      'EangService'
    )
    // Store secrets, env and schemas separately to maintain type safety
    const { secrets, secretsSchema, env, envSchema } = confToValidate
    const baseConf: any = { ...validatedConf }
    if (secrets !== undefined) baseConf.secrets = secrets
    if (secretsSchema !== undefined) baseConf.secretsSchema = secretsSchema
    if (env !== undefined) baseConf.env = env
    if (envSchema !== undefined) baseConf.envSchema = envSchema
    super(baseConf as EangConfig<TEnv, TSecrets>)
    this.instanceOf = validatedConf.instanceOf
    this.subscriptions = validatedConf.subscriptions
  }

  async start(): Promise<ConsumerStream> {
    if (!this._natsConnection) {
      this._natsConnection = await this._nss.initialize(this.natConf)
    }
    const stream = await this._nss.ensureConsumerStream(
      this.instanceOf,
      this.subscriptions
    )
    return stream
  }
}
